import { Vec3, vec } from './math';

/**
 * Every tunable of the bike + rider model. Units are SI. The coach-point tests in tests/sim/coach/ pin the
 * behaviour these numbers produce; docs/superpowers/plans/2026-09-28-plan-12-coach-physics.md maps them.
 */
export interface BikeConfig {
  mass: number;              // bike + rider, kg
  inertia: Vec3;             // body-frame diagonal: x pitch, y yaw, z roll
  wheelbase: number;
  wheelRadius: number;
  suspensionRest: number;    // mount to wheel centre at zero compression
  suspensionTravel: number;  // mean of the two (callers that do not care which end)
  forkTravel: number;        // m of front wheel travel
  shockTravel: number;       // m of rear wheel travel
  springRate: number;        // N/m per wheel (linear part)
  forkSpringScale: number;   // the fork's rate as a fraction of springRate (a softer front: it dives under the brakes)
  springProgression: number; // N/m^3 per wheel: rising rate (linkage, fork air) past progressionStart, so big hits stiffen
  progressionStart: number;  // fraction of the travel where the rising rate starts (the first part is linear: it moves)
  damping: number;           // N*s/m per wheel in compression, up to dampingKnee
  reboundDamping: number;    // N*s/m per wheel while extending (rebound: slower than compression, so it does not kick)
  dampingKnee: number;       // m/s of compression speed where the damping goes digressive (big hits bottom out)
  dampingHighSpeed: number;  // fraction of `damping` above the knee
  bumpStopStiffness: number;
  bumpStopDamping: number;   // N*s/m while on the bump stop (a hydraulic stop soaks the hit instead of bouncing)

  // Tyres: combined-slip friction circle (src/sim/physics/tyre.ts).
  grip: number;              // base friction coefficient, times SURFACE_GRIP
  slipPeak: number;          // slip ratio at peak longitudinal force
  slipAnglePeak: number;     // rad, slip angle at peak lateral force
  tyreShape: number;         // Pacejka C on the normalised combined slip; sliding keeps sin(C*pi/2) of the peak
  slipFloor: number;         // m/s added to |v| in slip ratio and slip angle so they stay finite at a standstill
  rearWheelInertia: number;  // kg*m^2, wheel + tyre + sprocket
  frontWheelInertia: number; // kg*m^2

  // Drivetrain (src/sim/physics/drivetrain.ts). Ratios and shift points match src/audio/model.ts DEFAULT_ENGINE.
  engineTorque: number;      // N*m peak at the crank
  torqueCurve: readonly (readonly [number, number])[]; // [rpm, fraction of peak]
  engineBrake: number;       // N*m at the crank at the limiter with the throttle shut
  partThrottleLoss: number;  // how fast a part-open throttle runs out of air with rpm (0 = none)
  engineInertia: number;     // kg*m^2 at the crank
  gearRatios: readonly number[]; // engine rpm per wheel rpm
  driveEfficiency: number;
  idleRpm: number;
  launchRpm: number;         // the auto clutch slips to hold about this in 1st
  shiftUpRpm: number;
  shiftDownRpm: number;
  limiterRpm: number;
  shiftTime: number;         // s of torque cut per shift

  // Brakes. The single beginner lever gives combinedBrakeTorque split combinedBrakeFront / rest.
  frontBrakeTorque: number;  // N*m at full front lever
  rearBrakeTorque: number;   // N*m at full rear lever
  combinedBrakeTorque: number;
  combinedBrakeFront: number;
  brakeRise: number;         // s time constant of brake pressure building
  brakeFall: number;         // s time constant of it releasing
  clutchPullRearBrake: number; // rear lever above this pulls the auto clutch (no stall, no engine inertia)

  rollingResistance: number; // fraction of wheel load on hard ground
  aeroDrag: number;          // N per (m/s)^2
  linearDrag: number;        // 1/s, residual on the ground
  angularDrag: number;       // 1/s on the ground; zero in the air (angular momentum is kept)

  maxSteer: number;          // rad at standstill
  steerFadeSpeed: number;    // m/s, phase 1 steering fade (the view's wheel animation still reads it)
  steerLateralAccel: number; // m/s^2 a full-steer turn asks for at speed (see steerAngleFor)
  leanKp: number;
  leanKd: number;
  maxLean: number;           // rad, cap on the lean controller's target roll
  balanceTorque: number;     // N*m the rider's balance (steering under the bike) gives at speed
  balanceSpeed: number;      // m/s where the balance torque is full; below it scales with speed (min 45 %)
  slideCatch: number;        // N*m/rad the rider steers into a rear slide with
  slideCatchMax: number;     // N*m cap
  slideRollOff: readonly [number, number]; // rad of sideslip over which the rider eases the throttle off
  countersteer: number;      // rad of extra steer into the lean per rad of lean past the wanted lean
  airRollKp: number;         // N*m/rad, in-air roll toward level
  airRollKd: number;         // N*m*s/rad
  airYawKp: number;          // N*m/rad the rider keeps the bike pointing along its flight
  airYawKd: number;          // N*m*s/rad
  whipYaw: number;           // N*m of yaw full left/right throws the bike with in the air
  whipRollMax: number;       // rad of lay-over full left/right asks for in the air (the whip's roll)
  whipEarly: number;         // s after leaving the ground in which the whip is thrown harder
  whipEarlyGain: number;     // x the whip torque in that window
  airBodyPitch: number;      // rad of bike pitch per unit of body travel in the air (mass exchange, no torque)

  // Rider (src/sim/physics/riderState.ts).
  riderComShift: number;     // m of COM travel along the bike at body = +-1
  wheelieRollOff: readonly [number, number];         // rad of wheelie over which the rider rolls the throttle off (full mode)
  wheelieRollOffBeginner: readonly [number, number]; // the same in beginner mode (earlier)
  stoppieRollOff: readonly [number, number];         // rad of tail-up over which the rider eases the front lever (Real)
  seatedComHeight: number;   // m the COM sits above the bike centre when seated
  standingComHeight: number; // m when standing on the pegs
  bodyRate: number;          // body-axis units per second the rider can move
  standRate: number;         // stance units per second (0 seated, 1 standing)
  standingDampingScale: number;
  standBodyReach: number;    // standing, the body reaches this much further forward (fraction): he can get his chest over the bars
  riderUpright: number;      // m of rider lateral offset per unit of sin(lean) when standing (his body stays more upright)
  seatedLimbDamping: number; // fraction of limbPitchDamping when seated (standing: all of it)
  riderMass: number;         // kg of `mass` that is the rider (the sprung mass on the seat or the pegs)
  seatStiffness: number;     // N/m coupling the rider through the seat (sitting)
  seatDamping: number;       // N*s/m
  pegStiffness: number;      // N/m through knees and ankles (standing)
  pegDamping: number;        // N*s/m
  legTravel: number;         // m the legs give either way before they are fully bent or straight
  legStopStiffness: number;  // N/m past legTravel
  lipRiseSpeed: number;      // m/s upward: the bike is leaving a ramp (not a wheelie on the flat)
  lipKickDamping: number;    // N*m*s/rad the legs soak a nose-down kick with only the rear on the ground
  limbPitchDamping: number;  // N*m*s/rad the rider's arms and legs resist pitching on the ground (standing; half seated)
  footMaxSpeed: number;      // m/s, the foot helps in turns below this
  footRollKp: number;        // N*m/rad of extra balance the foot gives at a standstill
  footTorque: number;        // N*m cap on what the foot can push
  footLeanBonus: number;     // rad of extra lean allowed with the foot out
  autoFootSteer: number;     // |steer| above this puts the foot out in beginner mode
  autoFootLean: number;      // rad of lean above this puts the foot out in beginner mode

  // Outcomes.
  washoutTime: number;       // s of saturated front lateral grip under braking or lean before the front is gone
  lockedFrontFallTime: number; // s a locked front can skid bolt upright before the bike tucks and goes down
  endoPitch: number;         // rad nose-down relative to the ground that throws the rider over the bars
  landClean: number;         // rad of nose error vs the slope a clean landing tolerates
  landHardSpeed: number;     // m/s into the ground: hard landing above this
  riderAbsorbSpeed: number;  // m/s of closing speed at the bump stop (or at the end of the legs' travel) a rider can absorb
  loopPitch: number;         // rad of nose above the ground, rear only, past which the bike loops over backwards
  rutWallGain: number;       // rut wall force per unit load per unit depth gradient
  rutGripLoss: number;       // front lateral grip lost per metre of rut depth when climbing a wall across it

  collision: {
    stiffness: number;       // N/m per capsule contact (times the obstacle's own stiffness)
    damping: number;         // N*s/m
    friction: number;        // along the obstacle surface
    standLift: number;       // m the rider capsule rises when standing
    minBodyHeight: number;   // m: rocks and bales lower than this are only ridden over by the wheels
    fallSpeed: number;       // m/s into something solid that throws the rider
    reportSpeed: number;     // m/s of approach reported as a hit (telemetry, coach, audio)
  };
  fallUpDot: number;         // body up . world up below this = fallen
  noCrash: boolean;          // owner 2026-10-06: the rider never comes off; a limit crossed starts a recovery instead
  recoverTime: number;       // s a recovery lasts (stabilising torque, eased throttle, the view shakes)
  recoverTorque: number;     // N*m the recovery stands the bike up and aligns it with its travel with
  chassisStiffness: number;  // N/m per chassis contact point
  chassisDamping: number;    // N*s/m per chassis contact point
  chassisFriction: number;   // friction coefficient body-on-ground
}

export const DEFAULT_BIKE: BikeConfig = {
  mass: 180,
  inertia: vec(60, 55, 18),
  wheelbase: 1.44,
  wheelRadius: 0.35,
  suspensionRest: 0.35,
  suspensionTravel: 0.28,
  forkTravel: 0.28,
  shockTravel: 0.28,
  springRate: 12000,
  forkSpringScale: 0.75,
  springProgression: 2400000,
  progressionStart: 0.5,
  damping: 2700,
  reboundDamping: 3600,
  dampingKnee: 0.8,
  dampingHighSpeed: 0.2,
  bumpStopStiffness: 150000,
  bumpStopDamping: 8000,

  grip: 1.05,
  slipPeak: 0.12,
  slipAnglePeak: 0.11,
  tyreShape: 1.5,
  slipFloor: 1,
  rearWheelInertia: 0.85,
  frontWheelInertia: 0.3,

  engineTorque: 24,
  torqueCurve: [[1500, 0.4], [4000, 0.62], [6500, 0.86], [8500, 1], [9800, 0.85]],
  engineBrake: 9,
  partThrottleLoss: 1,
  engineInertia: 0.002,
  gearRatios: [27, 19.5, 15, 12.3, 10.4],
  driveEfficiency: 0.9,
  idleRpm: 1500,
  launchRpm: 6000,
  shiftUpRpm: 8800,
  shiftDownRpm: 4200,
  limiterRpm: 9800,
  shiftTime: 0.08,

  frontBrakeTorque: 560,
  rearBrakeTorque: 380,
  combinedBrakeTorque: 420,
  combinedBrakeFront: 0.7,
  brakeRise: 0.1,
  brakeFall: 0.04,
  clutchPullRearBrake: 0.5,

  rollingResistance: 0.02,
  aeroDrag: 0.65,
  linearDrag: 0.02,
  angularDrag: 0.5,

  maxSteer: 0.6,
  steerFadeSpeed: 8,
  steerLateralAccel: 10,
  leanKp: 3000,
  leanKd: 600,
  maxLean: Math.PI / 4,
  balanceTorque: 1200,
  balanceSpeed: 8,
  slideCatch: 600,
  slideCatchMax: 450,
  slideRollOff: [0.45, 0.95],
  countersteer: 0.5,
  airRollKp: 600,
  airRollKd: 150,
  airYawKp: 150,
  airYawKd: 150,
  whipYaw: 260,
  whipRollMax: 0.6,
  whipEarly: 0.3,
  whipEarlyGain: 1.5,
  airBodyPitch: 0.1,

  riderComShift: 0.15,
  wheelieRollOff: [0.3, 0.55],
  wheelieRollOffBeginner: [0.2, 0.45],
  stoppieRollOff: [0.2, 0.45],
  seatedComHeight: 0.06,
  standingComHeight: 0.16,
  bodyRate: 5,
  standRate: 3,
  standingDampingScale: 1.3,
  standBodyReach: 0.5,
  riderUpright: 0.35,
  seatedLimbDamping: 0.3,
  riderMass: 75,
  seatStiffness: 110000,
  seatDamping: 2800,
  pegStiffness: 20000,
  pegDamping: 1700,
  legTravel: 0.14,
  legStopStiffness: 200000,
  lipRiseSpeed: 2,
  lipKickDamping: 2500,
  limbPitchDamping: 400,
  footMaxSpeed: 9,
  footRollKp: 1500,
  footTorque: 350,
  footLeanBonus: 0.12,
  autoFootSteer: 0.3,
  autoFootLean: 0.2,

  washoutTime: 0.5,
  lockedFrontFallTime: 1.5,
  endoPitch: 1.25,
  landClean: 0.26,
  landHardSpeed: 5,
  riderAbsorbSpeed: 8.5,
  loopPitch: 1.25,
  // Crash tolerance (owner 2026-10-06): a fall only from a clearly unrecoverable position; small and medium mistakes
  // slide, wobble and bottom out instead (washout 0.5 s, locked front 1.5 s, endo and loop 72 deg, 75 deg of roll).
  rutWallGain: 1.5,
  rutGripLoss: 3,

  collision: { stiffness: 250000, damping: 9000, friction: 0.5, standLift: 0.12, minBodyHeight: 0.4, fallSpeed: 4.5, reportSpeed: 1 },
  fallUpDot: 0.25,
  noCrash: true,
  recoverTime: 0.9,
  recoverTorque: 2200,
  chassisStiffness: 60000,
  chassisDamping: 4000,
  chassisFriction: 0.8,
};
